import { useState, useRef } from 'react';
import { buildExecutiveReasoning } from '../utils/buildExecutiveReasoning';
import { buildTerritorialScoring } from '../utils/buildTerritorialScoring';
import { buildTerritorialState } from '../utils/buildTerritorialState';

function getIVTLabel(ivt) {
  if (ivt >= 86) return 'Escalamiento — intervención inmediata';
  if (ivt >= 71) return 'Activación — gestión preventiva urgente';
  if (ivt >= 51) return 'Sensibilidad creciente — atención necesaria';
  if (ivt >= 31) return 'Observación — requiere monitoreo';
  return 'Estable — sin señales relevantes';
}

function getIVTColor(ivt) {
  if (ivt >= 86) return '#ef4444';
  if (ivt >= 71) return '#f97316';
  if (ivt >= 51) return '#eab308';
  if (ivt >= 31) return '#60a5fa';
  return '#22c55e';
}

// Confianza del IVT de ESTE actor, según cuántos campos cognitivos
// fueron declarados por el analista vs inferidos desde dropdowns.
function getConfianzaActor(scoring) {
  const completitud = scoring?._completitud;
  if (completitud === undefined || completitud === null) {
    return { nivel: 'Media', detalle: '' };
  }
  if (completitud >= 60) {
    return {
      nivel: 'Alta',
      detalle: `IVT sostenido por evidencia declarada (${completitud}% del perfil cognitivo completado por el analista).`
    };
  }
  if (completitud >= 20) {
    return {
      nivel: 'Media',
      detalle: `IVT parcialmente inferido (${completitud}% del perfil declarado, el resto deducido desde riesgo e influencia).`
    };
  }
  return {
    nivel: 'Baja',
    detalle: `IVT estimado principalmente desde dropdowns (solo ${completitud}% del perfil cognitivo declarado). Enriquecer el perfil del actor aumentaría la precisión.`
  };
}

function getConfianzaActorColor(nivel) {
  const colores = { 'Alta': '#22c55e', 'Media': '#eab308', 'Baja': '#f97316' };
  return colores[nivel] || '#64748b';
}

function getEventoColor(tipo) {
  if (!tipo) return '#67e8f9';
  const t = tipo.toLowerCase();
  if (t.includes('reuni') || t.includes('mesa')) return '#22c55e';
  if (t.includes('llamada')) return '#60a5fa';
  if (t.includes('alerta') || t.includes('conflict')) return '#ef4444';
  if (t.includes('visita') || t.includes('terreno')) return '#a78bfa';
  return '#67e8f9';
}

function getEntityLabel(entityType) {
  if (entityType === 'stakeholder') return 'Stakeholder territorial';
  if (entityType === 'alert') return 'Alerta territorial';
  if (entityType === 'signal') return 'Señal social / RRSS';
  if (entityType === 'event') return 'Evento territorial';
  return 'Entidad territorial';
}

function RelBox({ label, value }) {
  return (
    <div style={{
      background: 'rgba(103,232,249,0.07)',
      border: '1px solid rgba(103,232,249,0.15)',
      borderRadius: '12px',
      padding: '12px 8px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      fontSize: '11px',
      textAlign: 'center'
    }}>
      <strong style={{ fontSize: '18px', color: '#67e8f9' }}>{value}</strong>
      <span style={{ color: '#94a3b8' }}>{label}</span>
    </div>
  );
}

function TabButton({ active, label, onClick }) {
  return (
    <button
      style={{
        ...styles.tabButton,
        ...(active ? styles.tabButtonActive : {})
      }}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={styles.row}>
      <span style={styles.rowLabel}>{label}</span>
      <strong style={styles.rowValue}>{value}</strong>
    </div>
  );
}

function TerritorialPanel({
  selectedItem,
  onClose,
  projectActors = [],
  projectAlerts = [],
  projectSignals = [],
  projectTimeline = [],
  onRelatedSelect,
  onRelationsModeChange
}) {
  const [activeTab, setActiveTab] = useState('ficha');
  const closeTimer = useRef(null);

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => onClose(), 900);
  };

  const handleMouseEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  if (!selectedItem) return null;

  const {
    nombre, titulo, tipo, estado, riesgo, influencia,
    descripcion, region, comuna, sector, foco, actor,
    ubicacion, sentimiento, nivel, tags, panelType,
    fecha, red, entityType
  } = selectedItem;

  const relacional = selectedItem?._vita?.relacional || null;

  // Preferir SIEMPRE lo que ya calculó useVitaEngine (incluye enriquecimiento
  // con alertas y señales vinculadas). Solo recalcular como fallback para
  // entidades que no son actores procesados (alertas, señales, eventos).
  const yaProcesado = selectedItem?._vita?.scoring && selectedItem?._vita?.state;

  const territorialScoring = yaProcesado
    ? selectedItem._vita.scoring
    : buildTerritorialScoring(selectedItem);

  const territorialState = yaProcesado
    ? selectedItem._vita.state
    : buildTerritorialState(territorialScoring, selectedItem);

  const executiveReasoning = yaProcesado
    ? selectedItem._vita.reasoning
    : buildExecutiveReasoning(selectedItem);

  const timelineActor = projectTimeline.filter(evento =>
    String(evento.actor || '').toLowerCase().trim() ===
    String(nombre || '').toLowerCase().trim()
  );

  const señalesActor = projectSignals.filter(signal =>
    String(signal.actor || '').toLowerCase().trim() ===
    String(nombre || '').toLowerCase().trim()
  );

  const alertasActor = projectAlerts.filter(alerta =>
    String(alerta.actor || '').toLowerCase().trim() ===
    String(nombre || '').toLowerCase().trim()
  );

  return (
    <div
      style={styles.overlay}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <div style={styles.panel}>

        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <div style={styles.badge}>
              {panelType || 'TERRITORIAL INTELLIGENCE'}
            </div>
            <h2 style={styles.title}>
              {nombre || titulo || red || 'Registro territorial'}
            </h2>
            <div style={styles.entityTag}>
              {getEntityLabel(entityType)}
            </div>
          </div>
          <button style={styles.closeButton} onClick={onClose}>✕</button>
        </div>

        {/* ESTADO TERRITORIAL */}
        <div style={{
          ...styles.estadoBox,
          borderLeft: `6px solid ${territorialState.color}`
        }}>
          <div style={styles.estadoLabel}>ESTADO TERRITORIAL</div>
          <div style={{ ...styles.estadoValue, color: territorialState.color }}>
            {territorialState.state}
          </div>
          <div style={styles.estadoDesc}>{territorialState.description}</div>
        </div>

        {/* NARRATIVA */}
        <div style={styles.narrativaBox}>
          <div style={styles.miniLabel}>NARRATIVA DOMINANTE</div>
          <div style={styles.narrativaValue}>
            {executiveReasoning.narrativaDominante}
          </div>
        </div>

        {/* ACCIÓN SUGERIDA */}
        <div style={styles.accionBox}>
          <div style={styles.miniLabel}>ACCIÓN SUGERIDA</div>
          <div style={styles.accionValue}>
            {executiveReasoning.accionSugerida}
          </div>
        </div>

        {/* IVT */}
        <div style={styles.ivtBox}>
          <div style={{ flex: 1 }}>
            <div style={styles.ivtLabel}>IVT — Índice Viabilidad Territorial</div>
            <div style={styles.ivtNota}>{getIVTLabel(territorialScoring.IVT)}</div>
            {(() => {
              const conf = getConfianzaActor(territorialScoring);
              return (
                <div
                  style={{
                    ...styles.confianzaActorTag,
                    color: getConfianzaActorColor(conf.nivel),
                    borderColor: getConfianzaActorColor(conf.nivel)
                  }}
                  title={conf.detalle}
                >
                  Confianza del IVT: {conf.nivel}
                </div>
              );
            })()}
          </div>
          <div style={{ ...styles.ivtValue, color: getIVTColor(territorialScoring.IVT) }}>
            {territorialScoring.IVT}%
          </div>
        </div>

        {/* MOTOR RELACIONAL */}
        {relacional && relacional.grado > 0 && (
          <div style={styles.relacionalBox}>
            <div style={styles.relacionalTitle}>Motor Relacional</div>
            <div style={styles.relacionalGrid}>
              <RelBox label="Conexiones" value={relacional.grado} />
              <RelBox label="Centralidad" value={`${relacional.centralidad}%`} />
              <RelBox label="Nivel" value={relacional.nivelCentralidad} />
              <RelBox label="Presión" value={relacional.presionRelacional} />
            </div>

            {relacional.esPuente && (
              <div style={styles.puenteTag}>⚡ Actor puente territorial</div>
            )}

            <div style={styles.relacionadosLabel}>Conectado con</div>
            {relacional.actoresConectados.map((c, i) => (
              <div key={i} style={styles.conexionItem}>
                <span style={styles.conexionNombre}>{c.nombre}</span>
                <div style={styles.conexionMeta}>
                  <span style={{
                    ...styles.tipoTag,
                    background:
                      c.tipo === 'Convergencia' ? 'rgba(239,68,68,0.18)' :
                      c.tipo === 'Aliado' ? 'rgba(34,197,94,0.18)' :
                      c.tipo === 'Tensión' ? 'rgba(249,115,22,0.18)' :
                      'rgba(103,232,249,0.18)',
                    color:
                      c.tipo === 'Convergencia' ? '#f87171' :
                      c.tipo === 'Aliado' ? '#4ade80' :
                      c.tipo === 'Tensión' ? '#fb923c' :
                      '#67e8f9'
                  }}>
                    {c.tipo}
                  </span>
                  <span style={styles.ivtTag}>IVT {c.IVT}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PESTAÑAS */}
        <div style={styles.tabs}>
          <TabButton
            active={activeTab === 'ficha'}
            label="Ficha 360"
            onClick={() => {
              setActiveTab('ficha');
              onRelationsModeChange?.(false);
            }}
          />
          <TabButton
            active={activeTab === 'timeline'}
            label="Timeline"
            onClick={() => {
              setActiveTab('timeline');
              onRelationsModeChange?.(false);
            }}
          />
          <TabButton
            active={activeTab === 'alertas'}
            label={alertasActor.length > 0 ? `Alertas (${alertasActor.length})` : 'Alertas'}
            onClick={() => {
              setActiveTab('alertas');
              onRelationsModeChange?.(false);
            }}
          />
        </div>

        {/* FICHA 360 */}
        {activeTab === 'ficha' && (
          <div>
            <div style={styles.fichaGrid}>
              <InfoRow label="Tipo" value={tipo || panelType || '—'} />
              <InfoRow label="Estado" value={estado || '—'} />
              <InfoRow label="Riesgo" value={riesgo || nivel || '—'} />
              <InfoRow label="Influencia" value={influencia || '—'} />
              <InfoRow label="Región" value={region || '—'} />
              <InfoRow label="Comuna" value={comuna || ubicacion || '—'} />
              <InfoRow label="Sector" value={sector || '—'} />
              <InfoRow label="Foco" value={foco || '—'} />
              {sentimiento && <InfoRow label="Sentimiento" value={sentimiento} />}
              {actor && <InfoRow label="Actor relacionado" value={actor} />}
              {fecha && <InfoRow label="Fecha" value={fecha} />}
              {red && <InfoRow label="Canal" value={red} />}
            </div>

            {(descripcion || selectedItem.observaciones) && (
              <div style={styles.obsBox}>
                <div style={styles.miniLabel}>OBSERVACIONES</div>
                <p style={styles.obsText}>
                  {descripcion || selectedItem.observaciones}
                </p>
              </div>
            )}

            {(selectedItem.telefono || selectedItem.correo || selectedItem.whatsapp) && (
              <div style={styles.contactoBox}>
                <div style={styles.miniLabel}>CONTACTO</div>
                {selectedItem.telefono && <InfoRow label="Teléfono" value={selectedItem.telefono} />}
                {selectedItem.correo && <InfoRow label="Correo" value={selectedItem.correo} />}
                {selectedItem.whatsapp && <InfoRow label="WhatsApp" value={selectedItem.whatsapp} />}
                {selectedItem.facebook && <InfoRow label="Facebook" value={selectedItem.facebook} />}
                {selectedItem.instagram && <InfoRow label="Instagram" value={selectedItem.instagram} />}
                {selectedItem.xTwitter && <InfoRow label="X / Twitter" value={selectedItem.xTwitter} />}
              </div>
            )}

            {tags && (
              <div style={styles.tagsBox}>
                {String(tags).split(',').map(t => t.trim()).filter(Boolean).map((tag, i) => (
                  <span key={i} style={styles.tag}>#{tag}</span>
                ))}
              </div>
            )}

            {señalesActor.length > 0 && (
              <div style={styles.señalesBox}>
                <div style={styles.miniLabel}>SEÑALES RRSS VINCULADAS</div>
                {señalesActor.map((signal, i) => (
                  <div key={i} style={styles.señalItem}>
                    <div style={styles.señalTop}>
                      <strong style={{ color: 'white' }}>{signal.red || 'RRSS'}</strong>
                      <span style={{
                        ...styles.sentTag,
                        color: signal.sentimiento === 'Negativo' || signal.sentimiento === 'Crítico'
                          ? '#f87171' : signal.sentimiento === 'Positivo' ? '#4ade80' : '#94a3b8'
                      }}>
                        {signal.sentimiento}
                      </span>
                    </div>
                    {signal.texto && <p style={styles.señalTexto}>{signal.texto}</p>}
                    <div style={styles.señalMeta}>
                      {signal.fecha} · Riesgo: {signal.riesgo || '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TIMELINE */}
        {activeTab === 'timeline' && (
          <div>
            {timelineActor.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>📋</div>
                <div style={styles.emptyTitle}>Sin eventos registrados</div>
                <div style={styles.emptyText}>
                  Aquí aparecerán las reuniones, llamadas, visitas y gestiones realizadas con este stakeholder.
                </div>
              </div>
            ) : (
              <div>
                {timelineActor
                  .sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''))
                  .map((evento, i) => (
                    <div key={i} style={styles.eventoItem}>
                      <div style={{
                        ...styles.eventoDot,
                        background: getEventoColor(evento.tipo)
                      }} />
                      <div style={styles.eventoContent}>
                        <div style={styles.eventoFecha}>{evento.fecha || 'Sin fecha'}</div>
                        <div style={styles.eventoTitulo}>{evento.titulo}</div>
                        <div style={styles.eventoTipo}>{evento.tipo}</div>
                        {evento.descripcion && (
                          <p style={styles.eventoDesc}>{evento.descripcion}</p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* ALERTAS */}
        {activeTab === 'alertas' && (
          <div>
            {alertasActor.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>⚠️</div>
                <div style={styles.emptyTitle}>Sin alertas registradas</div>
                <div style={styles.emptyText}>
                  Aquí aparecerán las alertas territoriales vinculadas a este stakeholder.
                </div>
              </div>
            ) : (
              <div>
                {alertasActor
                  .sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''))
                  .map((alerta, i) => (
                    <div key={i} style={styles.señalItem}>
                      <div style={styles.señalTop}>
                        <strong style={{ color: 'white' }}>
                          {alerta.titulo || 'Alerta sin título'}
                        </strong>
                        <span style={{
                          ...styles.sentTag,
                          color:
                            alerta.nivel === 'Alto' || alerta.nivel === 'Crítico'
                              ? '#f87171'
                              : alerta.nivel === 'Medio'
                                ? '#fbbf24'
                                : '#94a3b8'
                        }}>
                          {alerta.nivel || '—'}
                        </span>
                      </div>
                      {alerta.descripcion && (
                        <p style={styles.señalTexto}>{alerta.descripcion}</p>
                      )}
                      <div style={styles.señalMeta}>
                        {alerta.fecha || 'Sin fecha'} · {alerta.tipo || 'Alerta'} · Estado: {alerta.estado || '—'}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '440px',
    height: '100vh',
    background: 'linear-gradient(180deg,#071a2f 0%,#081f39 100%)',
    borderLeft: '1px solid rgba(255,255,255,0.08)',
    zIndex: 9999,
    boxShadow: '-20px 0 40px rgba(0,0,0,0.35)',
    overflowY: 'auto'
  },
  panel: { padding: '24px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '18px',
    gap: '14px'
  },
  badge: {
    display: 'inline-block',
    padding: '5px 11px',
    borderRadius: '999px',
    background: 'rgba(6,182,212,0.12)',
    color: '#67e8f9',
    fontSize: '10px',
    fontWeight: '900',
    letterSpacing: '1px',
    marginBottom: '10px',
    border: '1px solid rgba(103,232,249,0.22)',
    textTransform: 'uppercase'
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '24px',
    lineHeight: '1.15'
  },
  entityTag: {
    display: 'inline-flex',
    marginTop: '8px',
    padding: '5px 10px',
    borderRadius: '999px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.6)',
    fontSize: '11px',
    fontWeight: '800'
  },
  closeButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: 'none',
    background: 'rgba(255,255,255,0.08)',
    color: 'white',
    cursor: 'pointer',
    fontSize: '15px',
    flexShrink: 0
  },
  estadoBox: {
    background: 'linear-gradient(180deg,#0b223f 0%,#081b33 100%)',
    borderRadius: '16px',
    padding: '18px',
    marginBottom: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
  },
  estadoLabel: {
    color: '#94a3b8',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '8px',
    fontWeight: '800'
  },
  estadoValue: {
    fontSize: '28px',
    fontWeight: '900',
    marginBottom: '6px',
    lineHeight: '1'
  },
  estadoDesc: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: '13px',
    lineHeight: '1.5'
  },
  narrativaBox: {
    background: 'rgba(103,232,249,0.08)',
    border: '1px solid rgba(103,232,249,0.18)',
    borderRadius: '14px',
    padding: '14px',
    marginBottom: '10px'
  },
  accionBox: {
    background: 'rgba(16,185,129,0.08)',
    border: '1px solid rgba(16,185,129,0.2)',
    borderRadius: '14px',
    padding: '14px',
    marginBottom: '12px'
  },
  miniLabel: {
    color: '#94a3b8',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '6px',
    fontWeight: '800'
  },
  narrativaValue: {
    color: '#67e8f9',
    fontSize: '15px',
    fontWeight: '700',
    lineHeight: '1.4'
  },
  accionValue: {
    color: '#4ade80',
    fontSize: '14px',
    fontWeight: '600',
    lineHeight: '1.5'
  },
  ivtBox: {
    background: 'linear-gradient(135deg,#0f172a,#071a2f)',
    border: '1px solid rgba(103,232,249,0.18)',
    borderRadius: '14px',
    padding: '16px',
    marginBottom: '14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  ivtLabel: {
    color: '#98a2b3',
    fontSize: '11px',
    textTransform: 'uppercase',
    fontWeight: '700'
  },
  ivtNota: {
    color: '#94a3b8',
    fontSize: '11px',
    marginTop: '4px'
  },
  confianzaActorTag: {
    display: 'inline-block',
    marginTop: '8px',
    padding: '3px 9px',
    borderRadius: '999px',
    border: '1px solid',
    fontSize: '10px',
    fontWeight: '800',
    letterSpacing: '0.3px',
    cursor: 'help',
    background: 'rgba(255,255,255,0.04)'
  },
  ivtValue: {
    fontSize: '36px',
    fontWeight: '900'
  },
  relacionalBox: {
    background: 'rgba(103,232,249,0.05)',
    border: '1px solid rgba(103,232,249,0.12)',
    borderRadius: '14px',
    padding: '16px',
    marginBottom: '16px'
  },
  relacionalTitle: {
    color: '#67e8f9',
    fontSize: '10px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '12px'
  },
  relacionalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4,1fr)',
    gap: '6px',
    marginBottom: '10px'
  },
  puenteTag: {
    background: 'rgba(251,191,36,0.12)',
    border: '1px solid rgba(251,191,36,0.25)',
    color: '#fbbf24',
    borderRadius: '8px',
    padding: '7px 10px',
    fontSize: '11px',
    fontWeight: 'bold',
    marginBottom: '10px'
  },
  relacionadosLabel: {
    color: '#94a3b8',
    fontSize: '10px',
    fontWeight: '800',
    textTransform: 'uppercase',
    marginBottom: '6px'
  },
  conexionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 10px',
    borderRadius: '8px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.06)',
    marginBottom: '5px'
  },
  conexionNombre: {
    color: 'white',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  conexionMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  tipoTag: {
    padding: '2px 8px',
    borderRadius: '999px',
    fontSize: '10px',
    fontWeight: 'bold'
  },
  ivtTag: {
    color: '#67e8f9',
    fontSize: '10px',
    fontWeight: 'bold'
  },
  tabs: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '8px',
    marginBottom: '18px'
  },
  tabButton: {
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.04)',
    color: 'rgba(255,255,255,0.5)',
    borderRadius: '10px',
    padding: '10px',
    fontSize: '13px',
    fontWeight: '900',
    cursor: 'pointer'
  },
  tabButtonActive: {
    background: 'linear-gradient(135deg,#0891b2,#0f766e)',
    color: 'white',
    border: '1px solid transparent',
    boxShadow: '0 0 16px rgba(6,182,212,0.25)'
  },
  fichaGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '14px'
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    borderRadius: '10px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.05)',
    gap: '12px'
  },
  rowLabel: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '12px'
  },
  rowValue: {
    color: 'white',
    fontSize: '13px',
    textAlign: 'right'
  },
  obsBox: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '12px'
  },
  obsText: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: '13px',
    lineHeight: '1.6',
    margin: 0
  },
  contactoBox: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  tagsBox: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '12px'
  },
  tag: {
    background: 'rgba(6,182,212,0.12)',
    color: '#67e8f9',
    padding: '5px 10px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: '700'
  },
  señalesBox: { marginTop: '4px' },
  señalItem: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '10px',
    padding: '12px',
    marginBottom: '8px'
  },
  señalTop: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '6px'
  },
  sentTag: { fontSize: '11px', fontWeight: '800' },
  señalTexto: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: '12px',
    lineHeight: '1.5',
    margin: '0 0 6px'
  },
  señalMeta: { color: 'rgba(255,255,255,0.4)', fontSize: '11px' },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '16px',
    border: '1px dashed rgba(103,232,249,0.18)'
  },
  emptyIcon: { fontSize: '32px', marginBottom: '12px' },
  emptyTitle: {
    color: 'white',
    fontWeight: '800',
    fontSize: '15px',
    marginBottom: '8px'
  },
  emptyText: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '13px',
    lineHeight: '1.6'
  },
  eventoItem: { display: 'flex', gap: '12px', marginBottom: '14px' },
  eventoDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    marginTop: '4px',
    flexShrink: 0,
    boxShadow: '0 0 8px rgba(103,232,249,0.4)'
  },
  eventoContent: { flex: 1 },
  eventoFecha: {
    color: '#94a3b8',
    fontSize: '11px',
    fontWeight: '700',
    marginBottom: '3px'
  },
  eventoTitulo: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '800',
    marginBottom: '2px'
  },
  eventoTipo: {
    color: '#67e8f9',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    marginBottom: '6px'
  },
  eventoDesc: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: '12px',
    lineHeight: '1.5',
    margin: 0
  }
};

export default TerritorialPanel;